import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useDeferredValue,
} from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Menu,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import styles from "./Settings.module.css";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import { useDispatch, useSelector } from "react-redux";
import { getUser } from "../../../helpers/sessionStorageHelper";
import {
  fetchUsersRequest,
  fetchLocationsRequest,
  deleteUserRequest,
  assignRolesLocationsRequest,
  selectUsers,
  selectLocations,
  selectSettingsLoading,
  selectSettingsError,
} from "../../../dux/settings";
import { LOCATIONS } from "../../../config/locations";
import ActivityLog from "../ActivityLog";
import EquipmentConfigTab from "./EquipmentConfigTab";
import WebhookActivityLogsTab from "./WebhookActivityLogsTab";
import FeedbacksTab from "./FeedbacksTab";

// ---------- small helper to run a callback after the browser paints (double RAF)
const afterPaint = (cb) =>
  requestAnimationFrame(() => requestAnimationFrame(cb));

// simple debounce hook for any value
function useDebouncedValue(value, delay = 400) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/* =========================================================
   MAIN: Settings shell — only mounts the active tab.
   ========================================================= */
const Settings = ({ selectedLocation }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const user = getUser();
  const isSuperAdmin = user?.roles?.includes("SUPER_ADMIN");
  const isAdmin =
    isSuperAdmin ||
    user?.roles?.includes("ADMIN") ||
    user?.roles?.includes("ADMIN_VIEW_ONLY");
  const hasSettingsAccess = isAdmin;

  const TAB_STORAGE_KEY = "settings_selected_tab";
  const [tabValue, setTabValue] = useState(0);

  const tabDefinitions = useMemo(
    () => [
      {
        label: "User Management",
        key: "user-management",
        visible: isSuperAdmin,
        render: () => <UserManagementTab isMobile={isMobile} />,
      },
      {
        label: "Notification Settings",
        key: "notification-settings",
        visible: isSuperAdmin,
        render: () => (
          <NotificationSettingsTab selectedLocation={selectedLocation} />
        ),
      },
      {
        label: "Activity Logs",
        key: "activity-logs",
        visible: isAdmin,
        render: () => (
          <ActivityLogsTab selectedLocation={selectedLocation} />
        ),
      },
      {
        label: "Webhook Activity",
        key: "webhook-activity",
        visible: isAdmin,
        render: () => (
          <WebhookActivityLogsTab selectedLocation={selectedLocation} />
        ),
      },
      {
        label: "Equipment Configs",
        key: "equipment-configs",
        visible: isAdmin,
        render: () => (
          <EquipmentConfigTab
            selectedLocation={selectedLocation}
            isSuperAdmin={isSuperAdmin}
          />
        ),
      },
      {
        label: "Feedbacks",
        key: "feedbacks",
        visible: isSuperAdmin,
        render: () => <FeedbacksTab />,
      },
    ],
    [isSuperAdmin, isAdmin, isMobile, selectedLocation]
  );

  const visibleTabs = useMemo(
    () => tabDefinitions.filter((tab) => tab.visible),
    [tabDefinitions]
  );

  useEffect(() => {
    try {
      const saved = localStorage.getItem(TAB_STORAGE_KEY);
      const parsed = Number(saved);
      if (
        Number.isInteger(parsed) &&
        parsed >= 0 &&
        parsed < visibleTabs.length
      ) {
        setTabValue(parsed);
      }
    } catch {}
  }, [visibleTabs.length]);

  const safeTabValue = Math.min(
    tabValue,
    Math.max(visibleTabs.length - 1, 0)
  );
  useEffect(() => {
    if (safeTabValue !== tabValue) {
      setTabValue(safeTabValue);
    }
  }, [safeTabValue, tabValue]);

  const handleTabChange = useCallback((_, newValue) => {
    setTabValue(newValue);
    try {
      localStorage.setItem(TAB_STORAGE_KEY, String(newValue));
    } catch {}
  }, []);

  if (!hasSettingsAccess) {
    return (
      <Box className={styles.paperContainer}>
        <Alert severity="error">
          Access denied. Only ADMIN or SUPER_ADMIN users can access settings.
        </Alert>
      </Box>
    );
  }

  return (
    <Box className={styles.settingsContainer}>
      <Typography variant="h4" gutterBottom className={styles.pageTitle}>
        System Settings
      </Typography>

      <Box className={styles.tabsContainer}>
        <Tabs
          value={safeTabValue}
          onChange={handleTabChange}
          variant="scrollable"
          textColor="primary"
          indicatorColor="primary"
          scrollButtons="auto"
          allowScrollButtonsMobile
          className={styles.tabsRoot}
        >
          {visibleTabs.map((tab) => (
            <Tab key={tab.key} label={tab.label} className={styles.tab} />
          ))}
        </Tabs>
      </Box>

      {/* Mount only the active tab to avoid unnecessary renders */}
      {visibleTabs[safeTabValue] && visibleTabs[safeTabValue].render()}
    </Box>
  );
};

export default Settings;

/* =========================================================
   TAB 1: User Management (memoized subtrees + memoized table)
   ========================================================= */

const UserManagementTab = React.memo(function UserManagementTab({ isMobile }) {
  const dispatch = useDispatch();
  const users = useSelector(selectUsers);
  const locations = useSelector(selectLocations);
  const loading = useSelector(selectSettingsLoading);
  const settingsError = useSelector(selectSettingsError);

  // initial fetch (users & locations)
  React.useEffect(() => {
    dispatch(fetchUsersRequest({})); // blank search on mount
    dispatch(fetchLocationsRequest());
  }, [dispatch]);

  // available roles (static)
  const availableRoles = React.useMemo(
    () => [
      {
        value: "USER",
        label: "User",
        description: "Basic user with booking access",
      },
      {
        value: "ADMIN",
        label: "Admin",
        description: "Location administrator with management access",
      },
      {
        value: "ADMIN_VIEW_ONLY",
        label: "Admin View Only",
        description: "View-only access to all bookings and data",
      },
      {
        value: "SUPER_ADMIN",
        label: "Super Admin",
        description: "System-wide administrator with full access",
      },
    ],
    []
  );

  // available locations (from API or fallback config)
  const availableLocations = React.useMemo(() => {
    if (locations && Array.isArray(locations) && (locations?.length || 0) > 0) {
      return (locations || []).map((loc) => ({
        value: loc.name,
        label: loc.display_name || loc.name,
        description: `Location: ${loc.displayName || loc.name}`,
      }));
    }
    return Object.entries(LOCATIONS).map(([key, loc]) => ({
      value: key,
      label: loc.displayName || loc.name,
      description: `Location: ${loc.displayName || loc.name}`,
    }));
  }, [locations]);

  // ---------- local UI state
  const [success, setSuccess] = React.useState("");
  const [error, setError] = React.useState("");

  const [searchTerm, setSearchTerm] = React.useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 400); // <— debounce here

  const [roleFilter, setRoleFilter] = React.useState("all");
  const [locationFilter, setLocationFilter] = React.useState("all");
  const [statusFilter, setStatusFilter] = React.useState("all");

  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize] = React.useState(50);

  const searchInputRef = React.useRef(null);
  React.useEffect(() => {
    if (searchInputRef.current) searchInputRef.current.focus();
  }, []);

  // avoid duplicate API calls between the initial mount fetch and the debounced effect
  const lastSentTermRef = React.useRef("__init__");

  // call API whenever the debounced term changes
  React.useEffect(() => {
    const term = (debouncedSearchTerm || "").trim();
    if (lastSentTermRef.current === term) return; // do nothing if we already sent this term
    dispatch(fetchUsersRequest({ search_term: term }));
    lastSentTermRef.current = term;
  }, [debouncedSearchTerm, dispatch]);

  // reset to page 1 on search/filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, roleFilter, locationFilter, statusFilter]);

  // filter users (keep client-side filters; search term also filters locally for instant UI)
  const filteredUsers = React.useMemo(() => {
    if (!users || !Array.isArray(users)) return [];
    return users.filter((user) => {
      if (!user || typeof user !== "object" || !user.id) return false;

      const term = (searchTerm || "").trim().toLowerCase();
      if (term) {
        const hay = `${user.first_name || ""} ${user.last_name || ""} ${
          user.email_address || ""
        } ${user.mobile_number || ""} ${user.id || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }

      if (roleFilter !== "all" && !user.roles?.includes(roleFilter))
        return false;
      if (locationFilter !== "all" && !user.locations?.includes(locationFilter))
        return false;
      if (statusFilter !== "all") {
        if (statusFilter === "active" && !user.is_active) return false;
        if (statusFilter === "inactive" && user.is_active) return false;
      }
      return true;
    });
  }, [users, searchTerm, roleFilter, locationFilter, statusFilter]);

  // paginate
  const paginatedUsers = React.useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, currentPage, pageSize]);

  const totalPages = Math.ceil((filteredUsers?.length || 0) / pageSize);

  // pressing Enter fetches immediately (bypasses debounce)
  const handleSearchKeyDown = React.useCallback(
    (e) => {
      if (e.key === "Enter") {
        const term = (searchTerm || "").trim();
        dispatch(fetchUsersRequest({ search_term: term }));
        lastSentTermRef.current = term; // prevent the debounced effect from firing again with same term
      }
    },
    [dispatch, searchTerm]
  );

  // selection for bulk actions
  const [selectedUsers, setSelectedUsers] = React.useState([]);
  const handleUserSelection = React.useCallback((userId, checked) => {
    setSelectedUsers((prev) =>
      checked ? [...prev, userId] : prev.filter((id) => id !== userId)
    );
  }, []);
  const handleSelectAllUsers = React.useCallback(
    (checked) => {
      if (checked) {
        setSelectedUsers(
          users && Array.isArray(users)
            ? users.map((u) => u?.id).filter(Boolean)
            : []
        );
      } else {
        setSelectedUsers([]);
      }
    },
    [users]
  );

  // dialogs & forms
  const [userDialogOpen, setUserDialogOpen] = React.useState(false);
  const [editingUser, setEditingUser] = React.useState(null);
  const [userForm, setUserForm] = React.useState({
    first_name: "",
    last_name: "",
    email_address: "",
    mobile_number: "",
    roles: [],
    locations: [],
    is_active: true,
  });

  const openUserDialog = React.useCallback((user = null) => {
    if (user) {
      setEditingUser(user);
      setUserForm({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email_address: user.email_address || "",
        mobile_number: user.mobile_number || "",
        roles: user.roles || [],
        locations: user.locations || [],
        is_active: user.is_active !== undefined ? user.is_active : true,
      });
    } else {
      setEditingUser(null);
      setUserForm({
        first_name: "",
        last_name: "",
        email_address: "",
        mobile_number: "",
        roles: [],
        locations: [],
        is_active: true,
      });
    }
    setUserDialogOpen(true);
  }, []);

  const handleUserFormChange = React.useCallback((field, value) => {
    setUserForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const saveUser = React.useCallback(async () => {
    try {
      const assignmentData = {
        mobile_number: userForm.mobile_number,
        roles: userForm.roles || [],
        locations: userForm.locations || [],
        is_active: userForm.is_active ?? true,
      };

      await dispatch(assignRolesLocationsRequest(assignmentData));
      setSuccess(
        editingUser ? "User updated successfully" : "User added successfully"
      );
      setUserDialogOpen(false);
      setEditingUser(null);

      const currentTerm = (searchTerm || "").trim();
      dispatch(fetchUsersRequest({ search_term: currentTerm }));
      lastSentTermRef.current = currentTerm;
    } catch (err) {
      setError("Failed to save user: " + err.message);
    }
  }, [userForm, editingUser, dispatch, searchTerm]);

  // delete user
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] =
    React.useState(false);
  const [userToDelete, setUserToDelete] = React.useState(null);
  const openDeleteConfirmDialog = React.useCallback((user) => {
    setUserToDelete(user);
    setDeleteConfirmDialogOpen(true);
  }, []);
  const closeDeleteConfirmDialog = React.useCallback(() => {
    setDeleteConfirmDialogOpen(false);
    setUserToDelete(null);
  }, []);
  const deleteUser = React.useCallback(
    async (userId) => {
      try {
        await dispatch(deleteUserRequest(userId));
        setSuccess("User deleted successfully");
        const currentTerm = (searchTerm || "").trim();
        dispatch(fetchUsersRequest({ search_term: currentTerm }));
        lastSentTermRef.current = currentTerm;
      } catch (err) {
        setError("Failed to delete user: " + err.message);
      }
    },
    [dispatch, searchTerm]
  );
  const confirmDeleteUser = React.useCallback(async () => {
    if (userToDelete && userToDelete.id) {
      try {
        await deleteUser(userToDelete.id);
        closeDeleteConfirmDialog();
      } catch {}
    }
  }, [userToDelete, deleteUser, closeDeleteConfirmDialog]);

  // quick menu
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [quickMenuAnchor, setQuickMenuAnchor] = React.useState(null);
  const openQuickMenu = React.useCallback((event, user) => {
    setQuickMenuAnchor(event.currentTarget);
    setSelectedUser(user);
  }, []);
  const closeQuickMenu = React.useCallback(() => {
    setQuickMenuAnchor(null);
    setSelectedUser(null);
  }, []);
  const quickAssignRole = React.useCallback(
    (role) => {
      if (!selectedUser) return;
      const assignmentData = {
        mobile_number: selectedUser.mobile_number,
        roles: [role],
        locations: selectedUser.locations || [],
        is_active: selectedUser.is_active,
      };
      dispatch(assignRolesLocationsRequest(assignmentData));
      closeQuickMenu();
    },
    [dispatch, closeQuickMenu, selectedUser]
  );
  const quickAssignLocation = React.useCallback(
    (location) => {
      if (!selectedUser) return;
      const assignmentData = {
        mobile_number: selectedUser.mobile_number,
        roles: selectedUser.roles || [],
        locations: [location],
        is_active: selectedUser.is_active,
      };
      dispatch(assignRolesLocationsRequest(assignmentData));
      closeQuickMenu();
    },
    [dispatch, closeQuickMenu, selectedUser]
  );

  // bulk assignment
  const [bulkAssignmentDialogOpen, setBulkAssignmentDialogOpen] =
    React.useState(false);
  const [bulkAssignmentData, setBulkAssignmentData] = React.useState({
    roles: [],
    locations: [],
    action: "add",
  });
  const openBulkAssignmentDialog = React.useCallback(() => {
    if ((selectedUsers?.length || 0) === 0) {
      setError("Please select users first");
      return;
    }
    setBulkAssignmentDialogOpen(true);
  }, [selectedUsers?.length]);
  const closeBulkAssignmentDialog = React.useCallback(() => {
    setBulkAssignmentDialogOpen(false);
    setBulkAssignmentData({ roles: [], locations: [], action: "add" });
  }, []);
  const handleBulkAssignment = React.useCallback(() => {
    const { roles, locations } = bulkAssignmentData;
    if ((roles?.length || 0) === 0 && (locations?.length || 0) === 0) {
      setError("Please select at least one role or location");
      return;
    }

    for (const userId of selectedUsers) {
      const user =
        users && Array.isArray(users)
          ? users.find((u) => u?.id === userId)
          : null;
      if (user) {
        const assignmentData = {
          mobile_number: user.mobile_number,
          roles:
            bulkAssignmentData.action === "replace" && (roles?.length || 0) > 0
              ? roles
              : (roles?.length || 0) > 0
              ? Array.from(new Set([...(user.roles || []), ...roles]))
              : user.roles || [],
          locations:
            bulkAssignmentData.action === "replace" &&
            (locations?.length || 0) > 0
              ? locations
              : (locations?.length || 0) > 0
              ? Array.from(new Set([...(user.locations || []), ...locations]))
              : user.locations || [],
          is_active: user.is_active,
        };
        dispatch(assignRolesLocationsRequest(assignmentData));
      }
    }
    closeBulkAssignmentDialog();
  }, [
    bulkAssignmentData,
    selectedUsers,
    users,
    dispatch,
    closeBulkAssignmentDialog,
  ]);

  // header helpers
  const getRoleDisplayInfo = React.useCallback(
    (roleValue) =>
      availableRoles.find((r) => r.value === roleValue) || {
        label: roleValue,
        description: "",
      },
    [availableRoles]
  );
  const getLocationDisplayInfo = React.useCallback(
    (locationValue) =>
      availableLocations.find((l) => l.value === locationValue) || {
        label: locationValue,
        description: "",
      },
    [availableLocations]
  );

  // success/error flashes (from assignRoles)
  React.useEffect(() => {
    if (loading && loading?.assignRoles === false) {
      if (settingsError?.assignRoles) {
        setError(
          "Failed to assign roles/locations: " + settingsError.assignRoles
        );
      } else if (!settingsError?.assignRoles) {
        setSuccess("Roles/locations assigned successfully");
      }
    }
  }, [loading?.assignRoles, settingsError?.assignRoles]);

  // stable callbacks for table
  const onEditRow = React.useCallback(
    (u) => openUserDialog(u),
    [openUserDialog]
  );
  const onDeleteRow = React.useCallback(
    (u) => openDeleteConfirmDialog(u),
    [openDeleteConfirmDialog]
  );

  // ---------- RENDER (unchanged from your current split structure)
  return (
    <>
      {/* … keep your notices, header, SearchAndFilters, UsersTable, Pagination, dialogs, etc.
          exactly as in the previous file you’re using now … */}

      {/* Search & Filters */}
      <SearchAndFilters
        isMobile={isMobile}
        searchInputRef={searchInputRef}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onSearchKeyDown={handleSearchKeyDown}
        roleFilter={roleFilter}
        setRoleFilter={setRoleFilter}
        locationFilter={locationFilter}
        setLocationFilter={setLocationFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        availableRoles={availableRoles}
        availableLocations={availableLocations}
        filteredCount={filteredUsers?.length || 0}
        currentPage={currentPage}
        pageSize={pageSize}
      />

      <UsersTable
        users={paginatedUsers}
        loading={!!loading?.users}
        onEdit={onEditRow}
        onDelete={onDeleteRow}
        onToggleSelect={handleUserSelection}
        onToggleSelectAll={handleSelectAllUsers}
        isMobile={isMobile}
      />

      {Math.ceil((filteredUsers?.length || 0) / pageSize) > 1 && (
        <PaginationBar
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
        />
      )}

      {/* Dialogs */}
      <UserDialog
        open={userDialogOpen}
        onClose={() => setUserDialogOpen(false)}
        editingUser={!!editingUser}
        userForm={userForm}
        onChange={handleUserFormChange}
        onSave={saveUser}
        availableRoles={availableRoles}
        availableLocations={availableLocations}
      />

      <DeleteConfirmDialog
        open={deleteConfirmDialogOpen}
        onClose={closeDeleteConfirmDialog}
        user={userToDelete}
        onConfirm={confirmDeleteUser}
      />

      <QuickAssignMenu
        anchorEl={quickMenuAnchor}
        open={Boolean(quickMenuAnchor)}
        onClose={closeQuickMenu}
        onAssignRole={quickAssignRole}
        onAssignLocation={quickAssignLocation}
        availableLocations={availableLocations}
        loadingAssign={!!loading?.assignRoles}
      />

      <BulkAssignmentDialog
        open={bulkAssignmentDialogOpen}
        onClose={closeBulkAssignmentDialog}
        selectedUsers={selectedUsers}
        users={users}
        data={bulkAssignmentData}
        setData={setBulkAssignmentData}
        onApply={handleBulkAssignment}
        availableRoles={availableRoles}
        availableLocations={availableLocations}
        loadingAssign={!!loading?.assignRoles}
      />
    </>
  );
});

/* ---------- Search & Filters (memoized so it won't re-render other parts) */
const SearchAndFilters = React.memo(function SearchAndFilters({
  isMobile,
  searchInputRef,
  searchTerm,
  setSearchTerm,
  onSearchKeyDown,
  roleFilter,
  setRoleFilter,
  locationFilter,
  setLocationFilter,
  statusFilter,
  setStatusFilter,
  availableRoles,
  availableLocations,
  filteredCount,
  currentPage,
  pageSize,
}) {
  return (
    <Box
      className={`${styles.searchFilterContainer} ${
        isMobile ? styles.searchFilterContainerMobile : ""
      }`}
    >
      <Box className={`${styles.formGridContainerFour}`}>
        <TextField
          inputRef={searchInputRef}
          autoFocus
          label="Search Users"
          placeholder="Search by name, email, mobile, or user ID"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={onSearchKeyDown}
          size="small"
          fullWidth
        />
        <FormControl size="small" fullWidth>
          <InputLabel>Filter by Role</InputLabel>
          <Select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            label="Filter by Role"
          >
            <MenuItem value="all">All Roles</MenuItem>
            {(availableRoles || []).map((role) => (
              <MenuItem key={role.value} value={role.value}>
                {role.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel>Filter by Location</InputLabel>
          <Select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            label="Filter by Location"
          >
            <MenuItem value="all">All Locations</MenuItem>
            {(availableLocations || []).map((location) => (
              <MenuItem key={location.value} value={location.value}>
                {location.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Filter by Status"
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="active">Active Only</MenuItem>
            <MenuItem value="inactive">Inactive Only</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box
        className={`${styles.resultsSummary} ${
          !isMobile ? styles.resultsSummaryDesktop : ""
        }`}
      >
        <Typography variant="body2" className={styles.typographyPrimary}>
          Showing {(currentPage - 1) * pageSize + 1}-
          {Math.min(currentPage * pageSize, filteredCount)} of {filteredCount}{" "}
          users
        </Typography>
        {(searchTerm ||
          roleFilter !== "all" ||
          locationFilter !== "all" ||
          statusFilter !== "all") && (
          <Button
            size="small"
            onClick={() => {
              setSearchTerm("");
              setRoleFilter("all");
              setLocationFilter("all");
              setStatusFilter("all");
              try {
                if (searchInputRef.current) searchInputRef.current.focus();
              } catch {}
            }}
            className={`${styles.clearFiltersButton} ${
              isMobile ? styles.clearFiltersButtonMobile : ""
            }`}
          >
            Clear Filters
          </Button>
        )}
      </Box>
    </Box>
  );
});

/* ---------- Users Table (memoized with custom compare) */
const UsersTable = React.memo(
  function UsersTable({
    users,
    loading,
    onEdit,
    onDelete,
    onToggleSelect,
    onToggleSelectAll,
    isMobile,
  }) {
    if (loading) {
      return (
        <Box className={styles.centeredContainer}>
          <CircularProgress />
        </Box>
      );
    }

    if (!users || users.length === 0) {
      return (
        <Box className={styles.emptyStateContainer}>
          <Typography variant="body1" className={styles.emptyStateText}>
            No users found.
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <Box className={styles.mobileScrollHint}>
          💡 Scroll horizontally to view all table columns on mobile
        </Box>
        <Box className={styles.desktopScrollHint}>
          💡 Use horizontal scroll to view all table columns
        </Box>

        <Box
          className={`${styles.tableContainerEnhanced} ${
            isMobile ? styles.tableContainerEnhancedMobile : ""
          }`}
        >
          <div className={styles.tableWrapper}>
            <TableContainer
              component={Paper}
              className={`${styles.tableContainer} ${
                isMobile ? styles.tableContainerMobile : ""
              }`}
              sx={{ maxWidth: "100vw", overflowX: "auto" }}
            >
              <Table
                className={`${styles.table} ${
                  isMobile ? styles.tableMobile : ""
                }`}
              >
                <TableHead>
                  <TableRow>
                    <TableCell className={styles.tableHeaderName}>
                      Name
                    </TableCell>
                    <TableCell className={styles.tableHeaderEmail}>
                      Email
                    </TableCell>
                    <TableCell className={styles.tableHeaderMobile}>
                      Mobile
                    </TableCell>
                    <TableCell className={styles.tableHeaderId}>
                      User ID
                    </TableCell>
                    <TableCell className={styles.tableHeaderRoles}>
                      Roles
                    </TableCell>
                    <TableCell className={styles.tableHeaderLocations}>
                      Locations
                    </TableCell>
                    <TableCell className={styles.tableHeaderStatus}>
                      Status
                    </TableCell>
                    <TableCell className={styles.tableHeaderActions}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(users || []).map((user) => {
                    if (!user || !user.id) return null;
                    return (
                      <TableRow
                        key={user.id}
                        className={
                          user.deleted_at
                            ? styles.tableRowDeleted
                            : styles.tableRowHover
                        }
                      >
                        <TableCell className={styles.tableCellBasic}>
                          <Typography
                            variant="body2"
                            className={styles.typographyName}
                          >
                            {user.first_name || ""} {user.last_name || ""}
                          </Typography>
                        </TableCell>
                        <TableCell className={styles.tableCellBasic}>
                          <Typography
                            variant="body2"
                            className={styles.typographyEmail}
                          >
                            {user.email_address || ""}
                          </Typography>
                        </TableCell>
                        <TableCell className={styles.tableCellBasic}>
                          <Typography
                            variant="body2"
                            className={styles.typographyMobile}
                          >
                            {user.mobile_number || ""}
                          </Typography>
                        </TableCell>
                        <TableCell className={styles.tableCellBasic}>
                          <Typography
                            variant="body2"
                            className={styles.typographyId}
                            title={`${user.first_name || ""} ${
                              user.last_name || ""
                            } - ${user.mobile_number || ""}`}
                            style={{ cursor: "help" }}
                          >
                            {user.id || ""}
                          </Typography>
                        </TableCell>
                        <TableCell className={styles.tableCellRoles}>
                          <Box className={styles.roleChipContainer}>
                            {user.roles && Array.isArray(user.roles)
                              ? user.roles.map((role) => (
                                  <Chip
                                    key={role}
                                    label={role}
                                    size="small"
                                    className={styles.roleChip}
                                  />
                                ))
                              : null}
                          </Box>
                        </TableCell>
                        <TableCell className={styles.tableCellBasic}>
                          <Box className={styles.roleChipContainer}>
                            {user.locations && Array.isArray(user.locations)
                              ? user.locations.map((location) => (
                                  <Chip
                                    key={location}
                                    label={location}
                                    size="small"
                                    className={styles.locationChip}
                                  />
                                ))
                              : null}
                          </Box>
                        </TableCell>
                        <TableCell className={styles.tableCellBasic}>
                          <Box>
                            <Chip
                              label={
                                user.deleted_at
                                  ? "Deleted"
                                  : user.is_active
                                  ? "Active"
                                  : "Inactive"
                              }
                              color={
                                user.deleted_at
                                  ? "error"
                                  : user.is_active
                                  ? "success"
                                  : "default"
                              }
                              size="small"
                              className={
                                user.deleted_at ? styles.deletedChip : ""
                              }
                            />
                            {user.deleted_at && (
                              <Typography
                                variant="caption"
                                className={styles.deletedDateText}
                              >
                                Deleted on{" "}
                                {new Date(user.deleted_at).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell className={styles.tableCellBasic}>
                          <Box className={styles.actionButtonsContainer}>
                            <IconButton
                              size="small"
                              onClick={() => onEdit(user)}
                              className={styles.editButton}
                            >
                              <EditIcon className={styles.iconSmall} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (user.id) {
                                  onDelete(user);
                                }
                              }}
                              disabled={!!user.deleted_at || !user.id}
                              className={
                                user.deleted_at || !user.id
                                  ? styles.deleteButtonDisabled
                                  : styles.deleteButton
                              }
                              title={
                                user.deleted_at
                                  ? "User is already deleted"
                                  : !user.id
                                  ? "Invalid user ID"
                                  : "Delete user"
                              }
                            >
                              <DeleteIcon className={styles.iconSmall} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Box>
      </>
    );
  },
  (prev, next) =>
    prev.loading === next.loading &&
    prev.users === next.users && // identity changes only when filtered/paginated users change
    prev.onEdit === next.onEdit &&
    prev.onDelete === next.onDelete &&
    prev.onToggleSelect === next.onToggleSelect &&
    prev.onToggleSelectAll === next.onToggleSelectAll &&
    prev.isMobile === next.isMobile
);

/* ---------- Pagination Bar (memo) */
const PaginationBar = React.memo(function PaginationBar({
  currentPage,
  totalPages,
  setCurrentPage,
}) {
  return (
    <Box className={styles.paginationContainer}>
      <Button
        size="small"
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(currentPage - 1)}
        className={styles.paginationButtonPrevious}
      >
        Previous
      </Button>

      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
        let pageNum;
        if (totalPages <= 5) {
          pageNum = i + 1;
        } else if (currentPage <= 3) {
          pageNum = i + 1;
        } else if (currentPage >= totalPages - 2) {
          pageNum = totalPages - 4 + i;
        } else {
          pageNum = currentPage - 2 + i;
        }

        return (
          <Button
            key={pageNum}
            size="small"
            variant="outlined"
            onClick={() => setCurrentPage(pageNum)}
            className={
              currentPage === pageNum
                ? styles.paginationButtonActive
                : styles.paginationButton
            }
          >
            {pageNum}
          </Button>
        );
      })}

      <Button
        size="small"
        disabled={currentPage === totalPages}
        onClick={() => setCurrentPage(currentPage + 1)}
        className={styles.paginationButtonNext}
      >
        Next
      </Button>
    </Box>
  );
});

/* ---------- User Dialog (memo) */
const UserDialog = React.memo(function UserDialog({
  open,
  onClose,
  editingUser,
  userForm,
  onChange,
  onSave,
  availableRoles,
  availableLocations,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: styles.dialogPaperLarge }}
    >
      <DialogTitle className={styles.dialogTitle}>
        {editingUser ? "Edit User" : "Add New User"}
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <Box className={styles.formGrid}>
          <TextField
            label="First Name"
            value={userForm.first_name}
            onChange={(e) => onChange("first_name", e.target.value)}
            fullWidth
            className={styles.textField}
          />
          <TextField
            label="Last Name"
            value={userForm.last_name}
            onChange={(e) => onChange("last_name", e.target.value)}
            fullWidth
            className={styles.textField}
          />
          <TextField
            label="Email Address"
            type="email"
            value={userForm.email_address}
            onChange={(e) => onChange("email_address", e.target.value)}
            fullWidth
            className={styles.textField}
          />
          <TextField
            label="Mobile Number"
            value={userForm.mobile_number}
            onChange={(e) => onChange("mobile_number", e.target.value)}
            fullWidth
            className={styles.textField}
          />
          <FormControl fullWidth>
            <InputLabel className={styles.selectLabel}>Roles</InputLabel>
            <Select
              multiple
              value={userForm.roles}
              onChange={(e) => onChange("roles", e.target.value)}
              renderValue={(selected) => (
                <Box className={styles.selectChipContainer}>
                  {selected && Array.isArray(selected)
                    ? selected.map((value) => (
                        <Chip
                          key={value}
                          label={value}
                          size="small"
                          className={styles.selectChipRole}
                        />
                      ))
                    : null}
                </Box>
              )}
              className={styles.select}
            >
              {availableRoles.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight="600"
                      color="#09213c"
                    >
                      {role.label}
                    </Typography>
                    <Typography variant="caption" color="#666" fontSize="11px">
                      {role.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel className={styles.selectLabel}>Locations</InputLabel>
            <Select
              multiple
              value={userForm.locations}
              onChange={(e) => onChange("locations", e.target.value)}
              renderValue={(selected) => (
                <Box className={styles.selectChipContainer}>
                  {selected && Array.isArray(selected)
                    ? selected.map((value) => (
                        <Chip
                          key={value}
                          label={value}
                          size="small"
                          className={styles.selectChipLocation}
                        />
                      ))
                    : null}
                </Box>
              )}
              className={styles.select}
            >
              {availableLocations.map((location) => (
                <MenuItem key={location.value} value={location.value}>
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight="600"
                      color="#09213c"
                    >
                      {location.label}
                    </Typography>
                    <Typography variant="caption" color="#666" fontSize="11px">
                      {location.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={userForm.is_active}
                onChange={(e) => onChange("is_active", e.target.checked)}
                className={styles.switch}
              />
            }
            label={
              <Typography className={styles.switchLabel}>Active</Typography>
            }
            className={styles.formControlLabelFullWidth}
          />
        </Box>
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <Button
          onClick={onClose}
          className={`${styles.dialogButton} ${styles.dialogButtonCancel}`}
        >
          <CancelIcon className={styles.marginRightMedium} /> Cancel
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          className={`${styles.dialogButton} ${styles.dialogButtonSave}`}
        >
          <SaveIcon className={styles.marginRightMedium} /> Save
        </Button>
      </DialogActions>
    </Dialog>
  );
});

/* ---------- Delete Confirmation (memo) */
const DeleteConfirmDialog = React.memo(function DeleteConfirmDialog({
  open,
  onClose,
  user,
  onConfirm,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="delete-dialog-title"
      aria-describedby="delete-dialog-description"
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: styles.deleteConfirmDialog }}
    >
      <DialogTitle
        id="delete-dialog-title"
        className={styles.deleteConfirmDialogTitle}
      >
        🗑️ Confirm User Deletion
      </DialogTitle>
      <DialogContent className={styles.deleteConfirmDialogContent}>
        <Box className={styles.deleteConfirmDialogWarning}>
          <Typography
            variant="h6"
            className={styles.deleteConfirmDialogWarningTitle}
          >
            ⚡ Warning: This action will mark the user as Inactive❗
          </Typography>
          <Typography
            variant="body1"
            className={styles.deleteConfirmDialogUserInfo}
          >
            You are about to delete the following user:
          </Typography>

          {user && (
            <Box className={styles.deleteConfirmDialogUserDetails}>
              <Typography
                variant="body2"
                className={styles.deleteConfirmDialogUserDetail}
              >
                <strong>Name:</strong> {user.first_name || ""}{" "}
                {user.last_name || ""}
              </Typography>
              <Typography
                variant="body2"
                className={styles.deleteConfirmDialogUserDetail}
              >
                <strong>Email:</strong> {user.email_address || "Not provided"}
              </Typography>
              <Typography
                variant="body2"
                className={styles.deleteConfirmDialogUserDetail}
              >
                <strong>Mobile:</strong> {user.mobile_number || "Not provided"}
              </Typography>
              <Typography
                variant="body2"
                className={styles.deleteConfirmDialogUserDetail}
              >
                <strong>User ID:</strong> {user.id || "Unknown"}
              </Typography>
              {user.roles && user.roles.length > 0 && (
                <Typography
                  variant="body2"
                  className={styles.deleteConfirmDialogUserDetail}
                >
                  <strong>Roles:</strong> {user.roles.join(", ")}
                </Typography>
              )}
              {user.locations && user.locations.length > 0 && (
                <Typography
                  variant="body2"
                  className={styles.deleteConfirmDialogUserDetail}
                >
                  <strong>Locations:</strong> {user.locations.join(", ")}
                </Typography>
              )}
            </Box>
          )}

          <Typography
            variant="body1"
            className={styles.deleteConfirmDialogWarningText}
          >
            This action will deactivate the user account, marking it as Inactive
            without permanently deleting the user from the system.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions className={styles.deleteConfirmDialogActions}>
        <Button
          onClick={onClose}
          className={styles.deleteConfirmDialogCancelButton}
          startIcon={<CancelIcon />}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          className={styles.deleteConfirmDialogDeleteButton}
          variant="contained"
          startIcon={<DeleteIcon />}
          autoFocus
        >
          Delete User
        </Button>
      </DialogActions>
    </Dialog>
  );
});

/* ---------- Quick Assign Menu (memo) */
const QuickAssignMenu = React.memo(function QuickAssignMenu({
  anchorEl,
  open,
  onClose,
  onAssignRole,
  onAssignLocation,
  availableLocations,
  loadingAssign,
}) {
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <MenuItem onClick={() => onAssignRole("USER")} disabled={loadingAssign}>
        <ListItemIcon>
          {loadingAssign ? <CircularProgress size={20} /> : <PersonIcon />}
        </ListItemIcon>
        <ListItemText
          primary="Assign USER Role"
          secondary="Basic user with booking access"
        />
      </MenuItem>
      <MenuItem onClick={() => onAssignRole("ADMIN")} disabled={loadingAssign}>
        <ListItemIcon>
          {loadingAssign ? <CircularProgress size={20} /> : <PersonIcon />}
        </ListItemIcon>
        <ListItemText
          primary="Assign ADMIN Role"
          secondary="Location administrator with management access"
        />
      </MenuItem>
      <MenuItem
        onClick={() => onAssignRole("ADMIN_VIEW_ONLY")}
        disabled={loadingAssign}
      >
        <ListItemIcon>
          {loadingAssign ? <CircularProgress size={20} /> : <VisibilityIcon />}
        </ListItemIcon>
        <ListItemText
          primary="Assign ADMIN_VIEW_ONLY Role"
          secondary="View-only access to all bookings and data"
        />
      </MenuItem>
      <MenuItem
        onClick={() => onAssignRole("SUPER_ADMIN")}
        disabled={loadingAssign}
      >
        <ListItemIcon>
          {loadingAssign ? <CircularProgress size={20} /> : <PersonIcon />}
        </ListItemIcon>
        <ListItemText
          primary="Assign SUPER_ADMIN Role"
          secondary="System-wide administrator with full access"
        />
      </MenuItem>
      <MenuItem divider />
      {availableLocations.map((location) => (
        <MenuItem
          key={location.value}
          onClick={() => onAssignLocation(location.value)}
          disabled={loadingAssign}
        >
          <ListItemIcon>
            {loadingAssign ? (
              <CircularProgress size={20} />
            ) : (
              <LocationOnIcon />
            )}
          </ListItemIcon>
          <ListItemText
            primary={`Assign ${location.label}`}
            secondary={location.description}
          />
        </MenuItem>
      ))}
    </Menu>
  );
});

/* ---------- Bulk Assignment Dialog (memo) */
const BulkAssignmentDialog = React.memo(function BulkAssignmentDialog({
  open,
  onClose,
  selectedUsers,
  users,
  data,
  setData,
  onApply,
  availableRoles,
  availableLocations,
  loadingAssign,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: "12px", sm: "16px" },
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          margin: { xs: "16px", sm: "32px" },
          maxWidth: { xs: "calc(100vw - 32px)", sm: "600px" },
          width: { xs: "calc(100vw - 32px)", sm: "600px" },
        },
      }}
    >
      <DialogTitle className={styles.bulkAssignmentDialogTitle}>
        Bulk Assignment for {selectedUsers?.length || 0} User(s)
      </DialogTitle>
      <DialogContent className={styles.bulkAssignmentDialogContent}>
        <Box className={styles.bulkAssignmentContentBox}>
          <Typography variant="subtitle1" gutterBottom>
            Select users to assign roles and locations:
          </Typography>
          <Box className={styles.bulkAssignmentUsersBox}>
            {(users || [])
              .filter((u) => u && u.id && (selectedUsers || []).includes(u.id))
              .map((u) => (
                <Chip
                  key={u.id}
                  label={`${u.first_name || ""} ${u.last_name || ""}`}
                  className={styles.bulkAssignmentUserChip}
                />
              ))}
          </Box>

          <FormControl fullWidth className={styles.formControlMarginBottom}>
            <InputLabel>Assignment Action</InputLabel>
            <Select
              value={data.action}
              onChange={(e) =>
                setData((prev) => ({ ...prev, action: e.target.value }))
              }
            >
              <MenuItem value="add">Add to existing</MenuItem>
              <MenuItem value="replace">Replace existing</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth className={styles.formControlMarginBottom}>
            <InputLabel>Roles to Assign</InputLabel>
            <Select
              multiple
              value={data.roles}
              onChange={(e) =>
                setData((prev) => ({ ...prev, roles: e.target.value }))
              }
              renderValue={(selected) => (
                <Box className={styles.flexWrapGapSmall}>
                  {selected && Array.isArray(selected)
                    ? selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))
                    : null}
                </Box>
              )}
            >
              {(availableRoles || []).map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {role.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {role.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth className={styles.formControlMarginBottom}>
            <InputLabel>Locations to Assign</InputLabel>
            <Select
              multiple
              value={data.locations}
              onChange={(e) =>
                setData((prev) => ({ ...prev, locations: e.target.value }))
              }
              renderValue={(selected) => (
                <Box className={styles.flexWrapGapSmall}>
                  {selected && Array.isArray(selected)
                    ? selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))
                    : null}
                </Box>
              )}
            >
              {(availableLocations || []).map((location) => (
                <MenuItem key={location.value} value={location.value}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {location.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {location.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <CancelIcon /> Cancel
        </Button>
        <Button
          onClick={onApply}
          variant="contained"
          disabled={
            ((data?.roles?.length || 0) === 0 &&
              (data?.locations?.length || 0) === 0) ||
            loadingAssign
          }
          startIcon={
            loadingAssign ? <CircularProgress size={20} /> : <SaveIcon />
          }
        >
          {loadingAssign ? "Applying..." : "Apply Assignment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

/* =========================================================
   TAB 2: Notification Settings (memoized)
   ========================================================= */

const NotificationSettingsTab = React.memo(function NotificationSettingsTab({
  selectedLocation,
}) {
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settings);
  const { notificationSettings } = settings || {};
  const adminEmailsFromStore = useSelector(
    (state) => state.settings.admin_emails
  );
  const adminPhonesFromStore = useSelector(
    (state) => state.settings.admin_phone_numbers
  );

  const [adminEmails, setAdminEmails] = useState([]);
  const [adminPhoneNumbers, setAdminPhoneNumbers] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationSettingsLoading, setNotificationSettingsLoading] =
    useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // confirm dialogs
  const [deleteEmailConfirmDialogOpen, setDeleteEmailConfirmDialogOpen] =
    useState(false);
  const [emailToDelete, setEmailToDelete] = useState("");
  const [deletePhoneConfirmDialogOpen, setDeletePhoneConfirmDialogOpen] =
    useState(false);
  const [phoneToDelete, setPhoneToDelete] = useState("");

  // sync store → local
  useEffect(() => {
    if (Array.isArray(adminEmailsFromStore))
      setAdminEmails(adminEmailsFromStore);
  }, [adminEmailsFromStore]);
  useEffect(() => {
    if (Array.isArray(adminPhonesFromStore))
      setAdminPhoneNumbers(adminPhonesFromStore);
  }, [adminPhonesFromStore]);

  const validateEmail = useCallback((email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !email.trim()) return "Email address is required";
    if (!emailRegex.test(email.trim()))
      return "Please enter a valid email address";
    if (email.length > 254) return "Email address is too long";
    return null;
  }, []);
  const validatePhoneNumber = useCallback((phoneNumber) => {
    if (!phoneNumber || !phoneNumber.trim())
      return "Please enter a valid 10-digit Indian mobile number";
    const cleanPhone = phoneNumber.replace(/[\s\-\(\)]/g, "");
    const indianMobileRegex = /^[6-9]\d{9}$/;
    if (!indianMobileRegex.test(cleanPhone))
      return "Please enter a valid 10-digit Indian mobile number";
    return null;
  }, []);

  const fetchNotificationSettings = useCallback(async () => {
    if (!selectedLocation) return;
    try {
      setNotificationSettingsLoading(true);
      setNotificationLoading(true);
      await dispatch({
        type: "FETCH_ADMIN_EMAILS",
        payload: { location: selectedLocation },
      });
      await dispatch({
        type: "FETCH_ADMIN_PHONES",
        payload: { location: selectedLocation },
      });
    } catch (err) {
      setError("Failed to fetch notification settings: " + err.message);
    } finally {
      setNotificationSettingsLoading(false);
      setNotificationLoading(false);
    }
  }, [selectedLocation, dispatch]);

  useEffect(() => {
    if (selectedLocation) {
      setNotificationSettingsLoading(true);
      fetchNotificationSettings();
    }
  }, [selectedLocation, fetchNotificationSettings]);

  const addAdminEmail = useCallback(async () => {
    if (!newEmail.trim() || !selectedLocation) return;
    const emailErr = validateEmail(newEmail);
    if (emailErr) {
      setError(emailErr);
      return;
    }
    if (adminEmails.includes(newEmail.trim())) {
      setError("This email address is already configured");
      return;
    }
    try {
      setNotificationLoading(true);
      await dispatch({
        type: "ADD_ADMIN_EMAIL",
        payload: { email: newEmail.trim(), location: selectedLocation },
      });
      setNewEmail("");
      await fetchNotificationSettings();
      setSuccess("Admin email added successfully");
    } catch (err) {
      setError("Failed to add admin email: " + err.message);
    } finally {
      setNotificationLoading(false);
    }
  }, [
    newEmail,
    selectedLocation,
    fetchNotificationSettings,
    validateEmail,
    adminEmails,
    dispatch,
  ]);

  const removeAdminEmail = useCallback(
    async (email) => {
      if (!selectedLocation) return;
      try {
        setNotificationLoading(true);
        await dispatch({
          type: "REMOVE_ADMIN_EMAIL",
          payload: { email, location: selectedLocation },
        });
        await fetchNotificationSettings();
        setSuccess("Admin email removed successfully");
      } catch (err) {
        setError("Failed to remove admin email: " + err.message);
      } finally {
        setNotificationLoading(false);
      }
    },
    [selectedLocation, fetchNotificationSettings, dispatch]
  );

  const addAdminPhoneNumber = useCallback(async () => {
    if (!newPhoneNumber.trim() || !selectedLocation) return;
    const phoneErr = validatePhoneNumber(newPhoneNumber);
    if (phoneErr) {
      setError(phoneErr);
      return;
    }
    const cleanPhone = newPhoneNumber.replace(/[\s\-\(\)]/g, "");
    if (adminPhoneNumbers.includes(cleanPhone)) {
      setError("This phone number is already configured");
      return;
    }
    try {
      setNotificationLoading(true);
      await dispatch({
        type: "ADD_ADMIN_PHONE",
        payload: { phone_number: cleanPhone, location: selectedLocation },
      });
      setNewPhoneNumber("");
      await fetchNotificationSettings();
      setSuccess("Admin phone number added successfully");
    } catch (err) {
      setError("Failed to add admin phone number: " + err.message);
    } finally {
      setNotificationLoading(false);
    }
  }, [
    newPhoneNumber,
    selectedLocation,
    fetchNotificationSettings,
    validatePhoneNumber,
    adminPhoneNumbers,
    dispatch,
  ]);

  const removeAdminPhoneNumber = useCallback(
    async (phoneNumber) => {
      if (!selectedLocation) return;
      try {
        setNotificationLoading(true);
        await dispatch({
          type: "REMOVE_ADMIN_PHONE",
          payload: { phone_number: phoneNumber, location: selectedLocation },
        });
        await fetchNotificationSettings();
        setSuccess("Admin phone number removed successfully");
      } catch (err) {
        setError("Failed to remove admin phone number: " + err.message);
      } finally {
        setNotificationLoading(false);
      }
    },
    [selectedLocation, fetchNotificationSettings, dispatch]
  );

  if (!selectedLocation) {
    return (
      <Box className={styles.paperContainer}>
        <Alert severity="info" className={styles.infoAlert}>
          <Typography variant="body1" className={styles.emptyStateText}>
            Please select a location to manage notification settings.
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (notificationSettingsLoading) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress
          size={80}
          thickness={4}
          sx={{ color: "rgb(253, 199, 11)", marginBottom: 3 }}
        />
        <Typography variant="h5" className={styles.loadingTitle}>
          Loading Notification Settings
        </Typography>
        <Typography variant="body1" className={styles.loadingSubtitle}>
          Please wait while we fetch the notification configuration for{" "}
          {selectedLocation.charAt(0).toUpperCase() + selectedLocation.slice(1)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={styles.notificationSettingsContainer}>
      {error && (
        <Alert
          severity="error"
          className={styles.alert}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          className={styles.alert}
          onClose={() => setSuccess("")}
        >
          {success}
        </Alert>
      )}

      {/* Admin Emails */}
      <Paper className={styles.notificationCard} elevation={2}>
        <Box className={styles.notificationCardHeader}>
          <Box className={styles.notificationCardIconContainer}>
            <Box className={styles.notificationCardIcon}>📧</Box>
          </Box>
          <Box className={styles.notificationCardTitleSection}>
            <Typography variant="h6" className={styles.notificationCardTitle}>
              Admin Email Notifications
            </Typography>
            <Typography
              variant="body2"
              className={styles.notificationCardDescription}
            >
              Add email addresses that will receive admin notifications for this
              location.
            </Typography>
          </Box>
        </Box>

        <Box className={styles.notificationCardContent}>
          <Box className={styles.addItemContainer}>
            <TextField
              label="Admin Email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Enter admin email address"
              size="small"
              className={styles.notificationInput}
              InputProps={{ className: styles.notificationInputField }}
            />
            <Button
              variant="contained"
              onClick={addAdminEmail}
              disabled={!newEmail.trim() || notificationLoading}
              startIcon={
                notificationLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <AddIcon />
                )
              }
              className={styles.addItemButton}
            >
              Add Email
            </Button>
          </Box>

          {(adminEmails?.length || 0) > 0 ? (
            <Box className={styles.currentItemsContainer}>
              <Typography
                variant="subtitle2"
                className={styles.currentItemsTitle}
              >
                Current Admin Emails:
              </Typography>
              <Box className={styles.itemsChipContainer}>
                {(adminEmails || []).map((email, index) => (
                  <Chip
                    key={index}
                    label={email}
                    onDelete={() => (
                      setDeleteEmailConfirmDialogOpen(true),
                      setEmailToDelete(email)
                    )}
                    deleteIcon={<DeleteIcon />}
                    className={styles.emailChip}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <Box className={styles.emptyItemsContainer}>
              <Typography variant="body2" className={styles.emptyItemsText}>
                No admin emails configured yet.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Admin Phones */}
      <Paper className={styles.notificationCard} elevation={2}>
        <Box className={styles.notificationCardHeader}>
          <Box className={styles.notificationCardIconContainer}>
            <Box className={styles.notificationCardIcon}>📱</Box>
          </Box>
          <Box className={styles.notificationCardTitleSection}>
            <Typography variant="h6" className={styles.notificationCardTitle}>
              Admin Phone Number Notifications
            </Typography>
            <Typography
              variant="body2"
              className={styles.notificationCardDescription}
            >
              Add phone numbers that will receive SMS notifications for this
              location.
            </Typography>
          </Box>
        </Box>

        <Box className={styles.notificationCardContent}>
          <Box className={styles.addItemContainer}>
            <TextField
              label="Admin Phone Number"
              value={newPhoneNumber}
              onChange={(e) => setNewPhoneNumber(e.target.value)}
              placeholder="Enter phone number (e.g., 7042009926)"
              size="small"
              className={styles.notificationInput}
              InputProps={{ className: styles.notificationInputField }}
            />
            <Button
              variant="contained"
              onClick={addAdminPhoneNumber}
              disabled={!newPhoneNumber.trim() || notificationLoading}
              startIcon={
                notificationLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <AddIcon />
                )
              }
              className={styles.addItemButton}
            >
              Add Phone Number
            </Button>
          </Box>

          {(adminPhoneNumbers?.length || 0) > 0 ? (
            <Box className={styles.currentItemsContainer}>
              <Typography
                variant="subtitle2"
                className={styles.currentItemsTitle}
              >
                Current Admin Phone Numbers:
              </Typography>
              <Box className={styles.itemsChipContainer}>
                {(adminPhoneNumbers || []).map((phone, index) => (
                  <Chip
                    key={index}
                    label={phone}
                    onDelete={() => (
                      setDeletePhoneConfirmDialogOpen(true),
                      setPhoneToDelete(phone)
                    )}
                    deleteIcon={<DeleteIcon />}
                    className={styles.phoneChip}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <Box className={styles.emptyItemsContainer}>
              <Typography variant="body2" className={styles.emptyItemsText}>
                No admin phone numbers configured yet.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Quick Actions */}
      <Paper className={styles.quickActionsCard} elevation={2}>
        <Box className={styles.quickActionsHeader}>
          <Typography variant="h6" className={styles.quickActionsTitle}>
            Quick Actions
          </Typography>
          <Typography
            variant="body2"
            className={styles.quickActionsDescription}
          >
            Common notification management tasks
          </Typography>
        </Box>

        <Box className={styles.quickActionsContent}>
          <Button
            variant="outlined"
            className={styles.quickActionButton}
            onClick={() => {
              setNewEmail("");
              setNewPhoneNumber("");
              afterPaint(() => {
                const emailInput = document.querySelector(
                  'input[type="email"]'
                );
                if (emailInput) emailInput.focus();
              });
            }}
          >
            <AddIcon className={styles.quickActionIcon} />
            Add Multiple Items
          </Button>

          <Button
            variant="outlined"
            className={styles.quickActionButton}
            onClick={() => {
              const settings = {
                location: selectedLocation,
                admin_emails: adminEmails,
                admin_phone_numbers: adminPhoneNumbers,
                export_date: new Date().toISOString(),
              };
              const dataStr = JSON.stringify(settings, null, 2);
              const dataBlob = new Blob([dataStr], {
                type: "application/json",
              });
              const url = URL.createObjectURL(dataBlob);
              const link = document.createElement("a");
              link.href = url;
              link.download = `notification-settings-${selectedLocation}-${
                new Date().toISOString().split("T")[0]
              }.json`;
              link.click();
              URL.revokeObjectURL(url);
            }}
          >
            <DownloadIcon className={styles.quickActionIcon} />
            Export Settings
          </Button>
        </Box>
      </Paper>

      {/* Delete confirmation dialogs */}
      <Dialog
        open={deleteEmailConfirmDialogOpen}
        onClose={() => setDeleteEmailConfirmDialogOpen(false)}
        aria-labelledby="delete-email-dialog-title"
        aria-describedby="delete-email-dialog-description"
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: styles.deleteConfirmDialog }}
      >
        <DialogTitle
          id="delete-email-dialog-title"
          className={styles.deleteConfirmDialogTitle}
        >
          🗑️ Confirm Email Deletion
        </DialogTitle>
        <DialogContent className={styles.deleteConfirmDialogContent}>
          <Box className={styles.deleteConfirmDialogWarning}>
            <Typography
              variant="h6"
              className={styles.deleteConfirmDialogWarningTitle}
            >
              ⚠️ Warning: This action cannot be undone!
            </Typography>
            <Typography
              variant="body1"
              className={styles.deleteConfirmDialogUserInfo}
            >
              You are about to remove the following email address from the
              notification settings:
            </Typography>
            {emailToDelete && (
              <Box className={styles.deleteConfirmDialogUserDetails}>
                <Typography
                  variant="body2"
                  className={styles.deleteConfirmDialogUserDetail}
                >
                  <strong>Email:</strong> {emailToDelete}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions className={styles.deleteConfirmDialogActions}>
          <Button
            onClick={() => setDeleteEmailConfirmDialogOpen(false)}
            className={styles.deleteConfirmDialogCancelButton}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await removeAdminEmail(emailToDelete);
              setDeleteEmailConfirmDialogOpen(false);
            }}
            className={styles.deleteConfirmDialogDeleteButton}
            variant="contained"
            startIcon={<DeleteIcon />}
            autoFocus
          >
            Delete Email
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deletePhoneConfirmDialogOpen}
        onClose={() => setDeletePhoneConfirmDialogOpen(false)}
        aria-labelledby="delete-phone-dialog-title"
        aria-describedby="delete-phone-dialog-description"
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: styles.deleteConfirmDialog }}
      >
        <DialogTitle
          id="delete-phone-dialog-title"
          className={styles.deleteConfirmDialogTitle}
        >
          🗑️ Confirm Phone Number Deletion
        </DialogTitle>
        <DialogContent className={styles.deleteConfirmDialogContent}>
          <Box className={styles.deleteConfirmDialogWarning}>
            <Typography
              variant="h6"
              className={styles.deleteConfirmDialogWarningTitle}
            >
              ⚠️ Warning: This action cannot be undone!
            </Typography>
            <Typography
              variant="body1"
              className={styles.deleteConfirmDialogUserInfo}
            >
              You are about to remove the following phone number from the
              notification settings:
            </Typography>
            {phoneToDelete && (
              <Box className={styles.deleteConfirmDialogUserDetails}>
                <Typography
                  variant="body2"
                  className={styles.deleteConfirmDialogUserDetail}
                >
                  <strong>Phone Number:</strong> {phoneToDelete}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions className={styles.deleteConfirmDialogActions}>
          <Button
            onClick={() => setDeletePhoneConfirmDialogOpen(false)}
            className={styles.deleteConfirmDialogCancelButton}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await removeAdminPhoneNumber(phoneToDelete);
              setDeletePhoneConfirmDialogOpen(false);
            }}
            className={styles.deleteConfirmDialogDeleteButton}
            variant="contained"
            startIcon={<DeleteIcon />}
            autoFocus
          >
            Delete Phone Number
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

/* =========================================================
   TAB 3: Activity Logs (memoized)
   ========================================================= */
const ActivityLogsTab = React.memo(function ActivityLogsTab({
  selectedLocation,
}) {
  return (
    <Box>
      <Box className={styles.sectionHeader}>
        <Typography variant="h6" className={styles.sectionTitle}>
          Activity Logs
        </Typography>
      </Box>
      <ActivityLog location={selectedLocation} />
    </Box>
  );
});
